import styled from '@emotion/styled'
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import catchError from '../../../components/global/CatchError'
import Filter, { FilterConfig } from '../../../components/global/Filter'
import Loader from '../../../components/global/Loader'
import Title from '../../../components/global/Title'
import { sweetAlertSuccess } from '../../../components/sweetalert/Alert'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../../hooks/useVerifyAdminToken'
import apiClient from '../../../services/apiClient'
import { AdminContainer, TitleWrapper } from '../users/AdminUsers'

type JobStatus = 'pending' | 'processing' | 'completed' | 'failed'

interface Job {
    id: number
    action: string
    payload: any
    status: JobStatus
    attempts: number
    createdAt: string
    updatedAt: string
    scheduledAt?: string
    lastError?: string
}

interface QueueStats {
    pending: number
    processing: number
    completed: number
    failed: number
    total: number
    paused: boolean
}

const AdminBadnetQueue = () => {
    const user = useContext(UserContext)
    const [jobs, setJobs] = useState<Job[]>([])
    const [stats, setStats] = useState<QueueStats | null>(null)
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [actionFilter, setActionFilter] = useState<string>('all')
    const [searchFilter, setSearchFilter] = useState<string>('')
    const [loading, setLoading] = useState(true)
    const [selectedJob, setSelectedJob] = useState<Job | null>(null)
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [newJobAction, setNewJobAction] = useState('sync_tournaments')
    const [newJobPayload, setNewJobPayload] = useState('')
    const [processing, setProcessing] = useState(false)

    const getPayloadPlaceholder = (action: string) => {
        switch (action) {
            case 'sync_tournaments':
                return '{\n  "ligueId": 3306,\n  "saveTranslations": false\n}'
            case 'sync_tournament':
                return '{\n  "badnetId": 12345\n}'
            case 'update_member_count':
                return '{\n  "badnetId": 12345\n}'
            default:
                return '{}'
        }
    }

    const handleActionChange = (action: string) => {
        setNewJobAction(action)
        setNewJobPayload(getPayloadPlaceholder(action))
    }

    const verifyToken = useVerifyAdminToken()
    const lng = useLangSelected()
    const { t } = useTranslation('translation')

    const filterConfig: FilterConfig = useMemo(
        () => ({
            category: {
                field: 'status',
                label: t('admin.badnet.queue.filter.status'),
                options: ['pending', 'processing', 'completed', 'failed'],
            },
            select: [
                {
                    field: 'action',
                    label: t('admin.badnet.queue.filter.action'),
                    options: [
                        { value: 'sync_tournaments', label: t('admin.badnet.queue.actions.syncTournaments') },
                        { value: 'sync_tournament', label: t('admin.badnet.queue.actions.syncTournament') },
                        { value: 'update_member_count', label: t('admin.badnet.queue.actions.updateMemberCount') },
                    ],
                },
            ],
            search: {
                placeholder: t('admin.badnet.queue.filter.searchPlaceholder'),
            },
        }),
        [t],
    )

    const loadStats = () => {
        apiClient
            .get('/badnet/queue/status')
            .then(({ data }) => {
                setStats(data.data)
            })
            .catch((err) => {
                console.error('Error loading stats:', err)
                catchError(err.response?.data || 'Erreur lors du chargement des statistiques', () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                })
            })
    }

    const loadJobs = useCallback(() => {
        setLoading(true)
        const params: any = { limit: 50 }
        if (statusFilter && statusFilter !== 'all') {
            params.status = statusFilter
        }

        apiClient
            .get('/badnet/queue/jobs', { params })
            .then(({ data }) => {
                let filteredJobs = data.data
                if (actionFilter && actionFilter !== 'all') {
                    filteredJobs = filteredJobs.filter((job: Job) => job.action === actionFilter)
                }
                if (searchFilter) {
                    const searchLower = searchFilter.toLowerCase()
                    filteredJobs = filteredJobs.filter((job: Job) =>
                        JSON.stringify(job.payload).toLowerCase().includes(searchLower),
                    )
                }
                setJobs(filteredJobs)
            })
            .catch((err) => {
                console.error('Error loading jobs:', err)
                catchError(err.response?.data || 'Erreur lors du chargement des jobs', () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                })
            })
            .finally(() => setLoading(false))
    }, [statusFilter, actionFilter, searchFilter])

    const handleFiltersChange = (newFilters: Record<string, any>) => {
        if (newFilters.status !== undefined) setStatusFilter(newFilters.status)
        if (newFilters.action !== undefined) setActionFilter(newFilters.action)
        if (newFilters.search !== undefined) setSearchFilter(newFilters.search)
    }

    useEffect(() => {
        verifyToken()
        loadStats()
        loadJobs()
    }, [loadJobs])

    const pauseQueue = async () => {
        verifyToken()
        try {
            await apiClient.post('/badnet/queue/pause')
            sweetAlertSuccess(t('admin.badnet.queue.pauseSuccess'))
            loadStats()
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la mise en pause', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        }
    }

    const resumeQueue = async () => {
        verifyToken()
        try {
            await apiClient.post('/badnet/queue/resume')
            sweetAlertSuccess(t('admin.badnet.queue.resumeSuccess'))
            loadStats()
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la reprise', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        }
    }

    const deleteOldJobs = async () => {
        verifyToken()
        try {
            const response = await apiClient.delete('/badnet/queue/old-jobs', { params: { days: 30 } })
            sweetAlertSuccess(`${response.data.data.count} ${t('admin.badnet.queue.deleteOldSuccess')}`)
            loadStats()
            loadJobs()
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la suppression', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        }
    }

    const processJobManually = async (jobId: number) => {
        verifyToken()
        setProcessing(true)
        try {
            await apiClient.post(`/badnet/queue/process-job/${jobId}`)
            sweetAlertSuccess(t('admin.badnet.queue.processJobSuccess'))
            loadStats()
            loadJobs()
        } catch (error: any) {
            catchError(error.response?.data || "Erreur lors de l'exécution", () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        } finally {
            setProcessing(false)
        }
    }

    const createJob = async () => {
        verifyToken()
        try {
            const payload = newJobPayload ? JSON.parse(newJobPayload) : {}
            await apiClient.post('/badnet/queue/create-job', {
                action: newJobAction,
                payload,
            })
            sweetAlertSuccess(t('admin.badnet.queue.createJobSuccess'))
            setCreateModalOpen(false)
            setNewJobAction('sync_tournaments')
            setNewJobPayload('')
            loadStats()
            loadJobs()
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la création', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        }
    }

    const openCreateModal = () => {
        setNewJobAction('sync_tournaments')
        setNewJobPayload(getPayloadPlaceholder('sync_tournaments'))
        setCreateModalOpen(true)
    }

    const getStatusColor = (status: JobStatus) => {
        switch (status) {
            case 'pending':
                return '#FFA500'
            case 'processing':
                return '#2196F3'
            case 'completed':
                return '#4CAF50'
            case 'failed':
                return '#F44336'
            default:
                return '#9E9E9E'
        }
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('fr-FR')
    }

    if (loading && !stats) return <Loader />

    return (
        <>
            <TitleWrapper>
                <Title dataTestId="admin-badnet-queue-title">{t('admin.badnet.queue.title')}</Title>
                <div className="d-flex gap-2 mb-2 flex-wrap">
                    {stats?.paused ? (
                        <Button
                            onClick={resumeQueue}
                            variant="contained"
                            sx={{
                                backgroundColor: '#4CAF50',
                                '&:hover': { backgroundColor: '#45a049' },
                                fontSize: '1.2rem',
                            }}
                        >
                            {t('admin.badnet.queue.resume')}
                        </Button>
                    ) : (
                        <Button
                            onClick={pauseQueue}
                            variant="contained"
                            sx={{
                                backgroundColor: '#FFA500',
                                '&:hover': { backgroundColor: '#e69500' },
                                fontSize: '1.2rem',
                            }}
                        >
                            {t('admin.badnet.queue.pause')}
                        </Button>
                    )}
                    <Button
                        onClick={openCreateModal}
                        variant="contained"
                        sx={{
                            backgroundColor: 'var(--primary-color, #B40000)',
                            '&:hover': { backgroundColor: '#a00000' },
                            fontSize: '1.2rem',
                        }}
                    >
                        {t('admin.badnet.queue.createJob')}
                    </Button>
                    <Button onClick={deleteOldJobs} variant="contained" color="error" sx={{ fontSize: '1.2rem' }}>
                        {t('admin.badnet.queue.deleteOld')}
                    </Button>
                </div>
                {stats && (
                    <StatsContainer>
                        <StatCard>
                            <StatLabel>{t('admin.badnet.queue.stats.total')}</StatLabel>
                            <StatValue>{stats.total}</StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>{t('admin.badnet.queue.stats.pending')}</StatLabel>
                            <StatValue color="#FFA500">{stats.pending}</StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>{t('admin.badnet.queue.stats.processing')}</StatLabel>
                            <StatValue color="#2196F3">{stats.processing}</StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>{t('admin.badnet.queue.stats.completed')}</StatLabel>
                            <StatValue color="#4CAF50">{stats.completed}</StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>{t('admin.badnet.queue.stats.failed')}</StatLabel>
                            <StatValue color="#F44336">{stats.failed}</StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>{t('admin.badnet.queue.stats.paused')}</StatLabel>
                            <StatValue color={stats.paused ? '#F44336' : '#4CAF50'}>
                                {stats.paused ? t('common.yes') : t('common.no')}
                            </StatValue>
                        </StatCard>
                    </StatsContainer>
                )}
            </TitleWrapper>
            <AdminContainer>
                <Filter config={filterConfig} onFiltersChange={handleFiltersChange} />
                {loading ? (
                    <Loader />
                ) : (
                    <JobsTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{t('admin.badnet.queue.table.id')}</TableHeader>
                                <TableHeader>{t('admin.badnet.queue.table.action')}</TableHeader>
                                <TableHeader>{t('admin.badnet.queue.table.status')}</TableHeader>
                                <TableHeader>{t('admin.badnet.queue.table.attempts')}</TableHeader>
                                <TableHeader>{t('admin.badnet.queue.table.createdAt')}</TableHeader>
                                <TableHeader>{t('common.actions')}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobs.map((job) => (
                                <TableRow key={job.id}>
                                    <TableCell>#{job.id}</TableCell>
                                    <TableCell>{job.action}</TableCell>
                                    <TableCell>
                                        <StatusBadge color={getStatusColor(job.status)}>{job.status}</StatusBadge>
                                    </TableCell>
                                    <TableCell>{job.attempts}</TableCell>
                                    <TableCell>{formatDate(job.createdAt)}</TableCell>
                                    <TableCell>
                                        <ActionButton
                                            onClick={() => setSelectedJob(job)}
                                            variant="outlined"
                                            size="small"
                                            sx={{ fontSize: '1rem', mr: 1 }}
                                        >
                                            {t('common.details')}
                                        </ActionButton>
                                        {job.status === 'pending' && (
                                            <ActionButton
                                                onClick={() => processJobManually(job.id)}
                                                variant="contained"
                                                size="small"
                                                disabled={processing}
                                                sx={{
                                                    fontSize: '1rem',
                                                    backgroundColor: 'var(--primary-color, #B40000)',
                                                    '&:hover': { backgroundColor: '#a00000' },
                                                }}
                                            >
                                                {t('admin.badnet.queue.execute')}
                                            </ActionButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </JobsTable>
                )}
            </AdminContainer>

            {/* Create Job Modal */}
            <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontSize: '2rem' }}>{t('admin.badnet.queue.createJobTitle')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2, mt: 1 }}>
                        <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>
                            {t('admin.badnet.queue.form.action')}
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            value={newJobAction}
                            onChange={(e) => handleActionChange(e.target.value)}
                            slotProps={{ select: { native: true } }}
                            sx={{ fontSize: '1.2rem' }}
                        >
                            <option value="sync_tournaments">{t('admin.badnet.queue.actions.syncTournaments')}</option>
                            <option value="sync_tournament">{t('admin.badnet.queue.actions.syncTournament')}</option>
                            <option value="update_member_count">
                                {t('admin.badnet.queue.actions.updateMemberCount')}
                            </option>
                        </TextField>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>
                            {t('admin.badnet.queue.form.payload')}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={newJobPayload}
                            onChange={(e) => setNewJobPayload(e.target.value)}
                            placeholder='{"ligueId": 3306, "saveTranslations": false}'
                            sx={{ fontSize: '1.2rem', fontFamily: 'monospace' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateModalOpen(false)} sx={{ fontSize: '1.2rem' }}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={createJob}
                        variant="contained"
                        sx={{
                            backgroundColor: 'var(--primary-color, #B40000)',
                            '&:hover': { backgroundColor: '#a00000' },
                            fontSize: '1.2rem',
                        }}
                    >
                        {t('common.create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Job Details Modal */}
            <Dialog open={!!selectedJob} onClose={() => setSelectedJob(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontSize: '2rem' }}>
                    {t('admin.badnet.queue.jobDetailsTitle')} #{selectedJob?.id}
                </DialogTitle>
                <DialogContent>
                    {selectedJob && (
                        <Box>
                            <DetailRow>
                                <DetailLabel>{t('admin.badnet.queue.table.action')}:</DetailLabel>
                                <DetailValue>{selectedJob.action}</DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>{t('admin.badnet.queue.table.status')}:</DetailLabel>
                                <DetailValue>
                                    <StatusBadge color={getStatusColor(selectedJob.status)}>
                                        {selectedJob.status}
                                    </StatusBadge>
                                </DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>{t('admin.badnet.queue.table.attempts')}:</DetailLabel>
                                <DetailValue>{selectedJob.attempts}</DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>{t('admin.badnet.queue.table.createdAt')}:</DetailLabel>
                                <DetailValue>{formatDate(selectedJob.createdAt)}</DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>{t('admin.badnet.queue.table.updatedAt')}:</DetailLabel>
                                <DetailValue>{formatDate(selectedJob.updatedAt)}</DetailValue>
                            </DetailRow>
                            {selectedJob.scheduledAt && (
                                <DetailRow>
                                    <DetailLabel>{t('admin.badnet.queue.table.scheduledAt')}:</DetailLabel>
                                    <DetailValue>{formatDate(selectedJob.scheduledAt)}</DetailValue>
                                </DetailRow>
                            )}
                            {selectedJob.lastError && (
                                <DetailRow>
                                    <DetailLabel>{t('admin.badnet.queue.table.lastError')}:</DetailLabel>
                                    <DetailValueError>{selectedJob.lastError}</DetailValueError>
                                </DetailRow>
                            )}
                            <DetailRow>
                                <DetailLabel>{t('admin.badnet.queue.table.payload')}:</DetailLabel>
                                <DetailValue>
                                    <pre style={{ fontSize: '1rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {JSON.stringify(selectedJob.payload, null, 2)}
                                    </pre>
                                </DetailValue>
                            </DetailRow>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedJob(null)} sx={{ fontSize: '1.2rem' }}>
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

const StatsContainer = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
`

const StatCard = styled(Card)`
    flex: 1;
    min-width: 120px;
    text-align: center;
`

const StatLabel = styled(Typography)`
    font-size: 1.2rem;
    color: #666;
    margin-bottom: 0.5rem;
`

const StatValue = styled(Typography)<{ color?: string }>`
    font-size: 2rem;
    font-weight: bold;
    color: ${(props) => props.color || '#333'};
`

const JobsTable = styled.div`
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const TableHead = styled.div`
    background: #f5f5f5;
    border-bottom: 2px solid #e0e0e0;
`

const TableRow = styled.div`
    display: grid;
    grid-template-columns: 80px 200px 120px 80px 200px 150px;
    padding: 1rem;
    border-bottom: 1px solid #e0e0e0;
    &:hover {
        background: #f9f9f9;
    }
`

const TableHeader = styled.div`
    font-weight: bold;
    font-size: 1.2rem;
    padding: 1rem;
`

const TableBody = styled.div``

const TableCell = styled.div`
    font-size: 1rem;
    padding: 0.5rem;
    overflow: hidden;
    text-overflow: ellipsis;
`

const StatusBadge = styled.span<{ color: string }>`
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    background-color: ${(props) => props.color};
    color: white;
    font-size: 0.9rem;
    font-weight: bold;
`

const ActionButton = styled(Button)`
    font-size: 1rem;
`

const DetailRow = styled.div`
    display: flex;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background: #f9f9f9;
    border-radius: 4px;
`

const DetailLabel = styled(Typography)`
    font-size: 1.2rem;
    font-weight: bold;
    min-width: 150px;
    color: #666;
`

const DetailValue = styled(Typography)`
    font-size: 1rem;
    flex: 1;
`

const DetailValueError = styled(Typography)`
    font-size: 1rem;
    flex: 1;
    color: #f44336;
`

export default AdminBadnetQueue
